// radio / choice — the same data rendered three ways, chosen by the config.
//
//   radio            compact pills, for a yes/no or a short inline switch
//   choice           selectable cards
//   choice + image   full-bleed illustrated cards, with the option's selling
//                    points revealed on hover and on selection
//
// The illustrated variant is what the hand-written landing screens used before
// they became config, and it is what makes these screens feel like a product
// rather than a form: a picture of the thing you're insuring, a badge telling
// you which option most people pick, and the detail appearing only when you
// show interest in it.
//
// `choice` steps that set `hideCta` advance the moment a card is picked, so the
// card *is* the navigation — which is why it earns this much visual weight.
import React, { useState } from "react";
import { Box, Button, Chip, Grid2, Skeleton, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { AnimatePresence, motion } from "framer-motion";
import FieldFrame from "./FieldFrame";
import useReferenceOptions from "../../reference/useReferenceOptions";
import { invalidateReference } from "../../reference/referenceProvider";

import ResponsiveImage from "../../components/elements/ResponsiveImage";
import { SIZES } from "../../utils/responsiveImage";
const MotionBox = motion.create(Box);

const GOLD = "#C9A777";
const INK = "#3d322b";

const ChoiceInput = ({ field, value, values, error, disabled, onChange }) => {
  const [retry, setRetry] = useState(0);
  const { options, loading, error: loadError } = useReferenceOptions(field.options, values, retry);
  const [hovered, setHovered] = useState(/** @type {string|null} */ (null));

  const asCards = field.type === "choice";
  // `compact` is set where there is no room for artwork — the quick-edit
  // popover on the navigation ribbon renders the same field in 360px.
  const compact = field.props?.compact;
  // Full-bleed artwork is opted into, never inferred from "this option has an
  // image". The illustrated card crops to a 566/748 portrait, so feeding it a
  // landscape icon with text baked in produces a cropped, unreadable mess —
  // which is exactly what happened when this was inferred.
  const illustrated =
    asCards && !compact && field.props?.illustrated && options.some((o) => o.image);

  const select = (next) => () => !disabled && onChange({ target: { value: next } });
  const keyActivate = (next) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      select(next)();
    }
  };

  const domain = Array.isArray(field.options) ? null : field.options?.domain;

  // These options often *are* the screen — a `choice` step with `hideCta`
  // advances when a card is picked, so an empty render is a dead end with
  // nothing to click and nothing explaining why. Say what happened and offer a
  // way out instead of showing blank space.
  if (loading || loadError) {
    return (
      <FieldFrame label={field.label} required={field.required} help={field.help} error={error}>
        {loadError ? (
          <Box
            role="alert"
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px dashed rgba(212,87,90,0.45)",
              bgcolor: "rgba(212,87,90,0.04)",
            }}
          >
            <Typography sx={{ color: INK, fontWeight: 600, fontSize: "0.9375rem" }}>
              We couldn't load these options
            </Typography>
            <Typography sx={{ color: "#7B6E66", fontSize: "0.875rem", mt: 0.5, lineHeight: 1.5 }}>
              This is usually a temporary connection problem. Your answers so far are saved.
            </Typography>
            <Button
              onClick={() => {
                if (domain) invalidateReference(domain);
                setRetry((n) => n + 1);
              }}
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 17 }} />}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 999,
                color: "#a1885e",
                "&:hover": { bgcolor: "rgba(201,167,119,0.10)" },
              }}
            >
              Try again
            </Button>
          </Box>
        ) : (
          <Grid2 container spacing={{ xs: 2, md: 2.5 }}>
            {[0, 1].map((i) => (
              <Grid2 key={i} size={{ xs: 12, sm: field.props?.cardCols || 6 }}>
                <Skeleton
                  variant="rounded"
                  sx={{ borderRadius: 4, width: "100%", pt: field.type === "choice" ? "132%" : 0, height: field.type === "choice" ? 0 : 44 }}
                />
              </Grid2>
            ))}
          </Grid2>
        )}
      </FieldFrame>
    );
  }

  return (
    <FieldFrame label={field.label} required={field.required} help={field.help} error={error}>
      <Box role="radiogroup" aria-label={field.label} aria-invalid={!!error}>
        {illustrated ? (
          <Grid2 container spacing={{ xs: 2, md: 2.5 }}>
            {options.map((option) => {
              const selected = value === option.value;
              // Selection keeps the card open, so the reasons you chose it stay
              // on screen instead of vanishing the instant you commit.
              const open = selected || hovered === option.value;

              return (
                <Grid2
                  key={option.value}
                  size={{ xs: 12, sm: field.props?.cardCols || 6 }}
                >
                  <Box
                    role="radio"
                    aria-checked={selected}
                    tabIndex={disabled ? -1 : 0}
                    onClick={select(option.value)}
                    onKeyDown={keyActivate(option.value)}
                    onMouseEnter={() => setHovered(option.value)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(option.value)}
                    onBlur={() => setHovered(null)}
                    sx={{
                      position: "relative",
                      height: "100%",
                      borderRadius: 4,
                      overflow: "hidden",
                      cursor: disabled ? "not-allowed" : "pointer",
                      bgcolor: "#fff",
                      border: "2px solid",
                      borderColor: selected ? GOLD : "rgba(201,167,119,0.22)",
                      boxShadow: selected
                        ? "0 18px 40px rgba(201,167,119,0.28)"
                        : "0 6px 18px rgba(123,110,102,0.08)",
                      transition:
                        "border-color .3s ease, box-shadow .3s ease, transform .3s ease",
                      transform: open && !disabled ? "translateY(-4px)" : "none",
                      "&:focus-visible": {
                        outline: "none",
                        boxShadow: `0 0 0 4px rgba(201,167,119,0.25), 0 18px 40px rgba(201,167,119,0.20)`,
                      },
                    }}
                  >
                    {/* Badges sit over the artwork, never over the copy. */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 14,
                        left: 14,
                        right: 14,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        pointerEvents: "none",
                      }}
                    >
                      {option.subtitle && (
                        <Chip
                          label={option.subtitle}
                          size="small"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.95)",
                            color: INK,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            letterSpacing: 0.6,
                            backdropFilter: "blur(6px)",
                            border: "1px solid rgba(201,167,119,0.30)",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {option.badge && !selected && (
                        <Chip
                          label={option.badge}
                          size="small"
                          sx={{
                            bgcolor: INK,
                            color: "#F7E6C4",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            letterSpacing: 0.8,
                            height: 24,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>

                    <AnimatePresence>
                      {selected && (
                        <MotionBox
                          key="tick"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 320, damping: 18 }}
                          sx={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            zIndex: 3,
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(135deg, #d3b48a 0%, #C9A777 50%, #b4966b 100%)",
                            boxShadow: "0 8px 18px rgba(201,167,119,0.45)",
                          }}
                        >
                          <CheckIcon sx={{ color: "#fff", fontSize: 22 }} />
                        </MotionBox>
                      )}
                    </AnimatePresence>

                    {/* The illustration, on the cream backdrop the brand uses. */}
                    <Box
                      sx={{
                        width: "100%",
                        aspectRatio: "566 / 748",
                        background: "linear-gradient(180deg, #fdf6ea 0%, #f7eada 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <ResponsiveImage
                        src={option.image}
                        alt=""
                        sizes={SIZES.choice}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transform: open ? "scale(1.04)" : "scale(1)",
                          transition: "transform .5s ease",
                        }}
                      />
                    </Box>

                    {/* Title + perks, over a scrim so the copy stays legible
                        whatever the artwork does underneath. */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        p: { xs: 2, md: 2.5 },
                        color: "#fff",
                        background:
                          "linear-gradient(180deg, rgba(20,14,8,0) 0%, rgba(20,14,8,0.55) 35%, rgba(20,14,8,0.88) 100%)",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: 17, md: 21 },
                            fontWeight: 700,
                            letterSpacing: 0.2,
                            lineHeight: 1.2,
                          }}
                        >
                          {option.label}
                        </Typography>
                        <MotionBox
                          animate={{ x: open ? 4 : 0 }}
                          transition={{ duration: 0.25 }}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.18)",
                            border: "1px solid rgba(255,255,255,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <ArrowForwardRoundedIcon sx={{ color: "#fff", fontSize: 18 }} />
                        </MotionBox>
                      </Stack>

                      <AnimatePresence initial={false}>
                        {open && (option.perks?.length || option.hint) && (
                          <MotionBox
                            key="perks"
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            transition={{ duration: 0.28, ease: "easeOut" }}
                            sx={{ overflow: "hidden" }}
                          >
                            {option.perks?.length ? (
                              <Stack spacing={0.75}>
                                {option.perks.map((perk) => (
                                  <Stack
                                    key={perk}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <CheckCircleOutlineIcon
                                      sx={{ color: "#F7E6C4", fontSize: 16, flexShrink: 0 }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.8125rem",
                                        color: "rgba(255,255,255,0.92)",
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {perk}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: "rgba(255,255,255,0.92)",
                                  lineHeight: 1.45,
                                }}
                              >
                                {option.hint}
                              </Typography>
                            )}
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </Box>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        ) : asCards ? (
          <Grid2 container spacing={1.5}>
            {options.map((option) => {
              const selected = value === option.value;
              const open = selected || hovered === option.value;
              return (
                <Grid2 key={option.value} size={{ xs: 12, sm: field.props?.cardCols || 6 }}>
                  <Box
                    role="radio"
                    aria-checked={selected}
                    tabIndex={disabled ? -1 : 0}
                    onClick={select(option.value)}
                    onKeyDown={keyActivate(option.value)}
                    onMouseEnter={() => setHovered(option.value)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(option.value)}
                    onBlur={() => setHovered(null)}
                    sx={{
                      position: "relative",
                      p: 2,
                      height: "100%",
                      borderRadius: 3,
                      cursor: disabled ? "not-allowed" : "pointer",
                      border: "1px solid",
                      borderColor: selected ? GOLD : "rgba(123,110,102,0.18)",
                      bgcolor: selected ? "rgba(201,167,119,0.08)" : "#fff",
                      boxShadow: selected
                        ? "0 10px 24px rgba(201,167,119,0.22)"
                        : "0 2px 8px rgba(123,110,102,0.06)",
                      transition: "all .2s cubic-bezier(.2,.8,.2,1)",
                      "&:hover": disabled
                        ? {}
                        : {
                            borderColor: "rgba(201,167,119,0.6)",
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 26px rgba(123,110,102,0.12)",
                          },
                      "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
                    }}
                  >
                    {selected && (
                      <CheckCircleIcon
                        sx={{ position: "absolute", top: 12, right: 12, fontSize: 20, color: GOLD }}
                      />
                    )}
                    {option.badge && !selected && (
                      <Chip
                        label={option.badge}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          bgcolor: INK,
                          color: "#F7E6C4",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          letterSpacing: 0.6,
                          height: 21,
                        }}
                      />
                    )}

                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      {option.image && (
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            borderRadius: 2.5,
                            background: "linear-gradient(180deg, #fdf6ea 0%, #f7eada 100%)",
                            border: "1px solid rgba(201,167,119,0.22)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            transform: open ? "scale(1.05)" : "scale(1)",
                            transition: "transform .3s ease",
                          }}
                        >
                          <ResponsiveImage
                            src={option.image}
                            alt=""
                            sizes="64px"
                            sx={{ width: "80%", height: "80%", objectFit: "contain" }}
                          />
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: INK, fontSize: "1rem", pr: 3 }}>
                          {option.label}
                        </Typography>
                        {option.hint && (
                          <Typography
                            sx={{ color: "#7B6E66", fontSize: "0.875rem", mt: 0.5, lineHeight: 1.5 }}
                          >
                            {option.hint}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* The same reveal the illustrated cards use, so a vertical
                        without portrait artwork still rewards a hover. */}
                    <AnimatePresence initial={false}>
                      {open && option.perks?.length > 0 && (
                        <MotionBox
                          key="perks"
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.26, ease: "easeOut" }}
                          sx={{ overflow: "hidden" }}
                        >
                          <Stack spacing={0.75}>
                            {option.perks.map((perk) => (
                              <Stack key={perk} direction="row" alignItems="center" spacing={1}>
                                <CheckCircleOutlineIcon
                                  sx={{ color: GOLD, fontSize: 15, flexShrink: 0 }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.8125rem", color: "#7B6E66", lineHeight: 1.4 }}
                                >
                                  {perk}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </MotionBox>
                      )}
                    </AnimatePresence>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {options.map((option) => {
              const selected = value === option.value;
              return (
                <Box
                  key={option.value}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={disabled ? -1 : 0}
                  onClick={select(option.value)}
                  onKeyDown={keyActivate(option.value)}
                  sx={{
                    px: 2,
                    py: 1,
                    minHeight: { xs: 44, tablet: 40 },
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 999,
                    cursor: disabled ? "not-allowed" : "pointer",
                    border: "1px solid",
                    borderColor: selected ? GOLD : "rgba(123,110,102,0.18)",
                    bgcolor: selected ? "rgba(201,167,119,0.10)" : "#fff",
                    color: INK,
                    fontWeight: selected ? 700 : 500,
                    fontSize: "0.9375rem",
                    transition: "all .2s ease",
                    "&:hover": disabled
                      ? {}
                      : { borderColor: "rgba(201,167,119,0.6)", bgcolor: "#fdf6ea" },
                    "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
                  }}
                >
                  {option.label}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </FieldFrame>
  );
};

export default ChoiceInput;
